import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BaseResponse } from '../models/auth.models';
import { ApiResponse } from '../models/product.models';
import {
  Store,
  StoreLiteDto,
  StoreSummary,
  CreateStoreRequest,
  UpdateStoreRequest,
  UpdateStoreBboxRequest,
  StoreListResponse,
} from '../models/store.models';
import { ApiService } from './api.service';
import { ApiCacheService } from '../cache/api-cache.service';

@Injectable({
  providedIn: 'root',
})
export class StoreService extends ApiService {
  private readonly endPoint = environment.apiUrl + '/api/Stores';
  private readonly apiCache = inject(ApiCacheService);

  /**
   * Do'konlar lug'ati — ilovaning eng ko'p so'raladigan ro'yxati: o'nga yaqin ekran
   * ochilishida so'raydi, ustiga `StoreContextService` har kirishda.
   *
   * Ro'yxat kamdan-kam o'zgaradi (do'kon qo'shish yiliga bir necha marta), shuning
   * uchun har ochilishda tarmoqni kutish o'rniga kesh beriladi. O'zgarganda kesh
   * quyidagi yozuv amallarida bekor qilinadi — ya'ni eski ro'yxat kutish bilan emas,
   * aniq hodisa bilan yangilanadi.
   */
  private readonly liteCache = this.apiCache.create<StoreLiteDto[]>({
    store: 'stores',
    key: 'lite',
    fetch: () =>
      this.http
        .get<ApiResponse<StoreLiteDto[]>>(`${this.endPoint}/all`)
        .pipe(map((res) => res.data ?? [])),
  });

  /**
   * Keshdan o'qiydi. Javob konverti (`success`/`errors`) qaytadan yig'iladi —
   * keshda faqat ma'lumotning o'zi saqlanadi, muvaffaqiyatsiz javobni keshlash
   * esa xatoni sahifa yangilangunicha yopishtirib qo'yardi.
   */
  getAllLite(): Observable<ApiResponse<StoreLiteDto[]>> {
    return this.liteCache.read().pipe(map((data) => ({ success: true, data, errors: [] })));
  }

  /** Do'kon ro'yxati o'zgarganda — keyingi o'qish serverdan keladi. */
  private invalidateLite<T>(): (source: Observable<T>) => Observable<T> {
    return (source) => source.pipe(tap(() => this.liteCache.invalidate()));
  }

  getStores(): Observable<BaseResponse<StoreListResponse>> {
    return this.http.get<BaseResponse<StoreListResponse>>(this.endPoint);
  }

  /** @param currency bo'sh bo'lsa backend tenant kotirovka valyutasini oladi (I11). */
  getStoreSummary(currency?: string): Observable<BaseResponse<StoreSummary[]>> {
    let params = new HttpParams();
    if (currency) params = params.set('currency', currency);
    return this.http.get<BaseResponse<StoreSummary[]>>(`${this.endPoint}/summary`, { params });
  }

  createStore(data: CreateStoreRequest): Observable<BaseResponse<Store>> {
    return this.http
      .post<BaseResponse<Store>>(this.endPoint, data)
      .pipe(this.invalidateLite<BaseResponse<Store>>());
  }

  updateStore(id: string, data: UpdateStoreRequest): Observable<BaseResponse<Store>> {
    return this.http
      .put<BaseResponse<Store>>(`${this.endPoint}/${id}`, data)
      .pipe(this.invalidateLite<BaseResponse<Store>>());
  }
  // eslint-disable-next-line
  deleteStore(id: string): Observable<BaseResponse<any>> {
    // eslint-disable-next-line
    const request = this.http.delete<BaseResponse<any>>(`${this.endPoint}/${id}`);
    return request.pipe(this.invalidateLite());
  }

  /**
   * Kesh ATAYLAB bekor qilinmaydi: xarita chegarasi `StoreLiteDto` ga kirmaydi,
   * ya'ni lug'at o'zgarmaydi. Bekor qilinsa har chegara tahriridan keyin ortiqcha
   * so'rov ketardi.
   */
  // eslint-disable-next-line
  updateBbox(id: string, data: UpdateStoreBboxRequest): Observable<BaseResponse<any>> {
    // eslint-disable-next-line
    return this.http.put<BaseResponse<any>>(`${this.endPoint}/${id}/bbox`, data);
  }
}
